import type {
  AffiliateParams,
  AffiliateData,
  AffiliateConfig,
  AffiliateEvent,
} from "./types";
import {
  ALL_AFFILIATE_PARAMETERS,
  STORAGE_CONFIG,
  ATTRIBUTION_MODELS,
  STORAGE_TYPES,
} from "./constants";

const DEFAULT_CONFIG: Required<AffiliateConfig> = {
  storageKey: STORAGE_CONFIG.DEFAULT_KEY,
  storageType: STORAGE_TYPES.SESSION,
  attribution: ATTRIBUTION_MODELS.LAST_TOUCH,
  ttl: STORAGE_CONFIG.DEFAULT_TTL,
  customParams: [],
  enableReferrerCapture: true,
  enableUserAgentCapture: false,
  onCapture: () => {},
  onError: () => {},
};

let currentConfig: Required<AffiliateConfig> = { ...DEFAULT_CONFIG };
let eventListeners: ((event: AffiliateEvent) => void)[] = [];

/**
 * Configure the affiliate tracker
 */
export function configureAffiliateTracker(
  config: Partial<AffiliateConfig> = {}
): void {
  currentConfig = { ...DEFAULT_CONFIG, ...config };
}

/**
 * Generate a unique session ID
 */
function generateSessionId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Get the appropriate storage object
 */
function getStorage(): Storage | null {
  if (typeof window === "undefined") return null;

  try {
    return currentConfig.storageType === "localStorage"
      ? window.localStorage
      : window.sessionStorage;
  } catch (error) {
    currentConfig.onError(new Error(`Storage not available: ${error}`));
    return null;
  }
}

/**
 * Check if stored data has expired
 */
function isExpired(timestamp: number): boolean {
  return Date.now() - timestamp > currentConfig.ttl;
}

/**
 * Emit an event to all listeners
 */
function emitEvent(event: AffiliateEvent): void {
  eventListeners.forEach((listener) => {
    try {
      listener(event);
    } catch (error) {
      currentConfig.onError(new Error(`Event listener error: ${error}`));
    }
  });
}

/**
 * Add an event listener
 */
export function addEventListener(
  listener: (event: AffiliateEvent) => void
): () => void {
  eventListeners.push(listener);

  // Return unsubscribe function
  return () => {
    const index = eventListeners.indexOf(listener);
    if (index > -1) {
      eventListeners.splice(index, 1);
    }
  };
}

/**
 * Capture affiliate parameters from URL and store them
 */
export function captureAffiliateParams(
  customConfig?: Partial<AffiliateConfig>
): AffiliateData | null {
  if (typeof window === "undefined") return null;

  // Temporarily override config if provided
  const originalConfig = currentConfig;
  if (customConfig) {
    currentConfig = { ...currentConfig, ...customConfig };
  }

  try {
    const storage = getStorage();
    if (!storage) return null;

    const urlParams = new URLSearchParams(window.location.search);
    const allKeys = [
      ...ALL_AFFILIATE_PARAMETERS,
      ...currentConfig.customParams,
    ];
    const affiliateParams: AffiliateParams = {};

    // Extract parameters from URL
    for (const key of allKeys) {
      const value = urlParams.get(key);
      if (value) {
        affiliateParams[key] = value;
      }
    }

    // Capture referrer if enabled and available
    if (currentConfig.enableReferrerCapture && document.referrer) {
      affiliateParams.referrer = document.referrer;
    }

    // Only proceed if we have affiliate data
    if (Object.keys(affiliateParams).length === 0) {
      return null;
    }

    // Check existing data for attribution logic
    const existingData = getAffiliateParams();
    let shouldUpdate = true;

    if (existingData && currentConfig.attribution === "first-touch") {
      // Don't update if we already have first-touch data
      shouldUpdate = false;
    }

    if (shouldUpdate) {
      const affiliateData: AffiliateData = {
        ...affiliateParams,
        timestamp: Date.now(),
        sessionId: generateSessionId(),
        attribution: currentConfig.attribution,
        url: window.location.href,
        ...(currentConfig.enableUserAgentCapture && {
          userAgent: navigator.userAgent,
        }),
      };

      storage.setItem(currentConfig.storageKey, JSON.stringify(affiliateData));

      // Emit capture event
      emitEvent({
        type: "capture",
        data: affiliateData,
        timestamp: Date.now(),
      });

      currentConfig.onCapture(affiliateData);
      return affiliateData;
    }

    return existingData;
  } catch (error) {
    const errorObj = error instanceof Error ? error : new Error(String(error));
    currentConfig.onError(errorObj);
    return null;
  } finally {
    // Restore original config
    if (customConfig) {
      currentConfig = originalConfig;
    }
  }
}

/**
 * Get stored affiliate parameters
 */
export function getAffiliateParams(): AffiliateData | null {
  if (typeof window === "undefined") return null;

  try {
    const storage = getStorage();
    if (!storage) return null;

    const stored = storage.getItem(currentConfig.storageKey);
    if (!stored) return null;

    const data: AffiliateData = JSON.parse(stored);

    // Check if data has expired
    if (isExpired(data.timestamp)) {
      clearAffiliateParams();
      emitEvent({
        type: "expire",
        data,
        timestamp: Date.now(),
      });
      return null;
    }

    return data;
  } catch (error) {
    const errorObj = error instanceof Error ? error : new Error(String(error));
    currentConfig.onError(errorObj);
    return null;
  }
}

/**
 * Clear stored affiliate parameters
 */
export function clearAffiliateParams(): void {
  if (typeof window === "undefined") return;

  try {
    const storage = getStorage();
    if (!storage) return;

    const existingData = getAffiliateParams();
    storage.removeItem(currentConfig.storageKey);

    // Emit clear event
    emitEvent({
      type: "clear",
      data: existingData || undefined,
      timestamp: Date.now(),
    });
  } catch (error) {
    const errorObj = error instanceof Error ? error : new Error(String(error));
    currentConfig.onError(errorObj);
  }
}

/**
 * Get affiliate parameters as URL search params string
 */
export function getAffiliateParamsAsUrlString(): string {
  const data = getAffiliateParams();
  if (!data) return "";

  const params = new URLSearchParams();

  // Only include the actual affiliate parameters, not metadata
  const affiliateKeys = [
    ...ALL_AFFILIATE_PARAMETERS,
    ...currentConfig.customParams,
  ];

  for (const key of affiliateKeys) {
    const value = data[key];
    if (typeof value === "string" && value) {
      params.set(key, value);
    }
  }

  return params.toString();
}

/**
 * Append affiliate parameters to a URL
 */
export function appendAffiliateParams(url: string): string {
  const affiliateParams = getAffiliateParamsAsUrlString();
  if (!affiliateParams) return url;

  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}${affiliateParams}`;
}

/**
 * Check if current session has affiliate data
 */
export function hasAffiliateData(): boolean {
  return getAffiliateParams() !== null;
}

/**
 * Get affiliate source (utm_source or referrer domain)
 */
export function getAffiliateSource(): string | null {
  const data = getAffiliateParams();
  if (!data) return null;

  if (data.utm_source) return data.utm_source;
  if (data.referrer) {
    try {
      return new URL(data.referrer).hostname;
    } catch {
      return data.referrer;
    }
  }

  return null;
}

/**
 * Get current configuration
 */
export function getConfig(): Required<AffiliateConfig> {
  return { ...currentConfig };
}

/**
 * Reset configuration to defaults
 */
export function resetConfig(): void {
  currentConfig = { ...DEFAULT_CONFIG };
}

// Export constants for external use
export { ALL_AFFILIATE_PARAMETERS, DEFAULT_CONFIG };
